import { Skeleton } from "primereact/skeleton";
import { Wordmark } from "../../components/wordmark/Wordmark";
import { ROUTES } from "../../routes";
import styles from "./Landing.module.css";
import landingImage from "./landingTextLight.svg";
import classNames from "classnames";
import dashboardImageTemp from "./pantheonInComp.png";
import { useNavigate } from "react-router-dom";
import { ContentSection, ScrollableLandingPage } from "./ScrollableLandingPage";
import { Message } from "primereact/message";

const LANDING_CONSTANTS = {
  Y_OFFSET: 100,
  DURATION: 0.65,
  EASE: "easeOut",
  MARGIN_1: "-100px",
  MARGIN_2: "-200px",
};

const FadeInText = () => {
  return (
    <>
      <img className={styles.headerTextImage} src={landingImage} />
      <h1 className={classNames(styles.headerText, styles.headerTextMobile)}>
        GIVING UNDERWRITING PROFESSIONALS{" "}
        <span className={styles.highlightColor}>SUPERPOWERS</span>
      </h1>
    </>
  );
};

export const LoadingDocument = () => {
  return (
    <div className={styles.loadingDocContainer}>
      <div className={styles.loadingDoc2}>
        <Wordmark />
        <span style={{ height: "16px" }} />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="60%" />
        <span style={{ height: "16px" }} />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="30%" />
      </div>
    </div>
  );
};

const InfoRow = () => {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoTitle}>HOW IT WORKS</span>
      <div className={styles.infoContent}>
        <div className={styles.infoBox}>
          <div className={styles.infoHeaderRow}>
            <div className={styles.infoHeaderBox}>1</div>
            <span className={styles.infoHeader}>Extraction</span>
          </div>
          <span>Extract policy information from email content</span>
        </div>
        <div className={styles.verticalLine} />
        <div className={styles.infoBox}>
          <div className={styles.infoHeaderRow}>
            <div className={styles.infoHeaderBox}>2</div>
            <span className={styles.infoHeader}>Organization</span>
          </div>
          <span>
            Filter, sort, and interact with policies on Pantheon's dashboard
          </span>
        </div>
        <div className={styles.verticalLine} />
        <div className={styles.infoBox}>
          <div className={styles.infoHeaderRow}>
            <div className={styles.infoHeaderBox}>3</div>
            <span className={styles.infoHeader}>Execution</span>
          </div>
          <span>
            Allow underwriters to dive into policy specifics with custom tools
          </span>
        </div>
      </div>
    </div>
  );
};

const TrustedBy = () => {
  return (
    <div className={styles.trustedByContainer}>
      <span className={styles.trustedByHeader}>TRUSTED BY</span>
      <div className={styles.trustedByLine} />
      <div className={styles.trustedByRow}>
        <Message
          text="Leading MGA with over 300MM in premiums"
          icon="pi pi-building"
          className={styles.trustedByMessage}
        />
        <Message
          text="200 year old mutual insurance company"
          icon="pi pi-building-columns"
          className={styles.trustedByMessage}
        />
        <Message
          text="MGA controlling 50% market share in its field"
          icon="pi pi-briefcase"
          className={styles.trustedByMessage}
        />
      </div>
    </div>
  );
};

export const Landing: React.FC<{ withInitialAnimation: boolean }> = ({
  withInitialAnimation,
}) => {
  const navigate = useNavigate();

  const sections: ContentSection[] = [
    {
      id: "automate-submissions",
      type: "text",
      content: {
        header: "INSURANCE IS CHANGING",
        highlightText: "CHANGING",
        infoText: [
          "The technology of the future has arrived.",
          "Pantheon is here to handle the tedious tasks in your underwriting workflow. Allow your professionals to spend time on work that matters.",
          //"Let Pantheon automate your process by parsing policy forms, broker emails, SOV spreadsheets, loss history, and more.",
        ],
      },
      style: {
        marginTop: "100px",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "loading-document",
      type: "component",
      isFinalComponentInRow: true,
      content: {
        component: <LoadingDocument />,
      },
      style: {
        alignItems: "center",
        flexDirection: "column",
        display: "flex",
        marginTop: "100px",
      },
      margin: LANDING_CONSTANTS.MARGIN_2,
    },
    {
      id: "solution-header",
      type: "text",
      isFinalComponentInRow: true,
      content: {
        header: "STREAMLINE YOUR FLOW",
        highlightText: "STREAMLINE",
        infoText: [
          "Pantheon’s AI Assistant is designed specifically for commercial insurance, streamlining complex document processing and connecting fragmented systems to modernize underwriting and operations at scale.",
        ],
      },
      style: {
        alignItems: "center",
        textAlign: "center",
        width: "840px",
        marginTop: "60px",
        marginBottom: "-60px",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "dashboard-image",
      type: "image",
      isFinalComponentInRow: true,
      content: {
        imageSrc: dashboardImageTemp,
        imageClassName: styles.imgFit,
      },
      style: { width: "800px", marginTop: "-100px" },
      margin: LANDING_CONSTANTS.MARGIN_2,
    },
    {
      id: "content-row",
      type: "component",
      isFinalComponentInRow: true,
      content: {
        component: <InfoRow />,
      },
      style: {
        width: "100vw",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "additional-content",
      type: "text",
      isFinalComponentInRow: true,
      content: {
        header: "ALL YOUR TOOLS IN ONE PLACE",
        highlightText: "ONE PLACE",
        infoText: [
          "Once your data lives in Pantheon, the possibilities are endless.",
          "Quickly generate referral templates from policy information, integrate with a rating workflow, or identify dense accumulations of TIV across all your policies.",
        ],
      },
      style: {
        alignItems: "center",
        textAlign: "center",
        width: "620px",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "trusted-by",
      type: "component",
      isFinalComponentInRow: true,
      content: {
        component: <TrustedBy />,
      },
      style: {
        width: "800px",
      },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
    {
      id: "contact-cta",
      type: "cta",
      isFinalComponentInRow: true,
      content: {
        header: "GET STARTED TODAY",
        highlightText: "TODAY",
        infoText:
          "Ready to reach out and see what Pantheon can do for your business? We are always open to building custom solutions and would love to chat.",
        button: {
          label: "GET STARTED",
          outlined: true,
          className: styles.contactButton,
          onClick: () => navigate(ROUTES.CONTACT),
        },
      },
      style: { alignItems: "center", textAlign: "center" },
      margin: LANDING_CONSTANTS.MARGIN_1,
    },
  ];

  return (
    <ScrollableLandingPage
      gap={96}
      withInitialAnimation={withInitialAnimation}
      sections={sections}
      headerComponent={FadeInText}
      styles={styles}
      constants={LANDING_CONSTANTS}
    />
  );
};
